import { HttpErrorResponse } from '@angular/common/http';
import { Inject, inject, Injectable, Optional, signal, computed } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { collectionData, Query } from '@angular/fire/firestore';
import { TranslateService } from '@ngx-translate/core';
import {
  AIMode,
  Conversation,
  ConversationApiService,
  ConversationChannel,
  EvaluateResponseRequestInterface,
  EvaluationSentiment,
  EventType,
  GetAvailableChannelsForConversationResponse,
  GetMessageRequest,
  GlobalParticipantType,
  MediaInterface,
  Message,
  MessageStatus,
  MessageType,
  MetadataIdentifier,
  Participant,
  ParticipantType as pType,
  PlatformLocation,
  SendMessageResponse,
} from '@vendasta/conversation';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { GetAccountInfoResponse, ParticipantType, SmsService } from '@vendasta/smsv2';
import {
  asyncScheduler,
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  firstValueFrom,
  map,
  merge,
  Observable,
  observeOn,
  of,
  shareReplay,
  skip,
  skipWhile,
  startWith,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { AlertOptions, ConversationChannelService } from '../channels/conversation-channel.abstract';
import { getMainConversationId } from '../channels/message-errors.util';
import { conversationUnseen, fromFirestoreId, toFirestoreId } from '../conversation-utils';
import { FirestoreService } from '../firestore.service';
import { INVALID_CONTACT_NAMES, LOCAL_STORAGE_ID } from '../inbox.constants';
import { InboxService } from '../inbox.service';
import { resolveCurrentAiMode, toAiResponseMode } from '../ai-mode.util';
import {
  ConversationAvailableChannels,
  ConversationDetail,
  ConversationMessage,
  FirestoreConversation,
  FirestoreEvent,
  Media,
  MessageInfo,
} from '../interface/conversation.interface';
import { HostAppInterface } from '../interface/host-app.interface';
import { convertMessageIntoConversationMessage } from '../message-utils';
import { ParticipantService } from '../participant.service';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  CONVERSATION_CHANNEL_SERVICE_TOKEN,
  CONVERSATION_CONFIG_TOKEN,
  CONVERSATION_CONVERSATION_CHANNELS_ENABLED_TOKEN,
  CONVERSATION_COUNTRY_TOKEN,
  CONVERSATION_EMAIL_ENABLED_TOKEN,
  CONVERSATION_HOST_APP_INTERFACE_TOKEN,
  MARKET_ID_TOKEN,
  PARTNER_ID_TOKEN,
  USER_ID_TOKEN,
} from '../tokens';
import { Event } from '@vendasta/conversation';
import { AiAssistantService, ConnectionType } from '@galaxy/ai-assistant';
import { Assistant, AssistantType, Namespace } from '@vendasta/protos/ai_assistants/v1alpha1';
import { listConnections$ } from '@vendasta/protos/ai_assistants/v1alpha1/connection-service';
import { AiResponder } from '../interface/assistant.interface';
import { ConversationStatelessService } from '../conversation-stateless.service';
import { PersonalEmailContentService } from '../channels/personal-email-content.service';
import {
  assignConversation$,
  cancelAIResponder$,
  getMultiEvents$,
  setLastSeen$,
  updateConversation$,
  AIResponseMode,
  ConversationChannelValue,
  EventTypeValue,
  Event as ProtoEvent,
  Participant as ProtoParticipant,
  SetLastSeenRequestStatus,
} from '@vendasta/protos/conversation/v1';
import { PaginatedConversationMessagesService } from './paginated-conversation-messages.service';

/**
 * ConversationService is responsible for managing conversation state and operations
 * within an Inbox instance. This service handles the core functionality needed
 * for conversations including:
 *
 * - Managing current conversation selection and state
 * - Managing conversation messages/events
 * - Providing conversation availability for different channels
 *
 * Each instance of this service maintains its own independent state, allowing
 * multiple Inbox instances to operate concurrently with separate conversation contexts.
 */
@Injectable()
export class ConversationService {
  private currentConversationChannel = ConversationChannel.CONVERSATION_CHANNEL_UNDEFINED;

  private readonly conversationSelected$$ = new BehaviorSubject<boolean>(false);
  readonly conversationSelected$ = this.conversationSelected$$.asObservable();

  /**
   * Without local overrides, conversations briefly flicker back to
   * unread between the user action and the next RPC emission.
   */
  private readonly localReadOverrides$$ = new BehaviorSubject<Set<string>>(new Set());

  /**
   * Without local overrides, a status change (close or reopen) lingers in the
   * inbox views until the eventually-consistent index catches up.
   */
  private readonly localOpenStatusOverrides$$ = new BehaviorSubject<Map<string, boolean>>(new Map());
  readonly localOpenStatusOverrides$ = this.localOpenStatusOverrides$$.asObservable();

  readonly currentParticipant$ = this.participantService.currentParticipant$;
  private readonly currentFirestoreConversationId$$: BehaviorSubject<string> = new BehaviorSubject('');
  readonly currentFirestoreConversationId$ = this.currentFirestoreConversationId$$.pipe(
    map(toFirestoreId),
    distinctUntilChanged(),
  );

  // The currentConversationDetail$ has to emit whenever firestoreService.conversation$ emits
  // this enables realtime updates in the current conversation whenever something changes
  // this.currentParticipant$ is used to ensure the current participant is loaded along with the conversation detail
  readonly currentConversationDetail$: Observable<ConversationDetail | null> = combineLatest([
    this.currentFirestoreConversationId$,
    this.currentParticipant$,
    this.partnerId$,
  ]).pipe(
    switchMap(([id, _, partnerId]) => {
      if (!id) return of(null);
      const disableParticipantBranding = partnerId === 'VMF';
      return merge(
        this.conversationStatelessService.getConversationDetail(fromFirestoreId(id), disableParticipantBranding),
        this.firestoreService.conversation$(id).pipe(
          skip(1),
          switchMap(() =>
            this.conversationStatelessService.getConversationDetail(fromFirestoreId(id), disableParticipantBranding),
          ),
        ),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
    skipWhile((v) => v === null),
  );

  readonly currentConvoSMSNumber$ = this.currentConversationDetail$.pipe(
    switchMap((convoDetail) => {
      if (
        !this.inboxService.isBusinessApp ||
        !convoDetail?.conversation.subjectParticipants.find(
          (p) => p.participantType === GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_CUSTOMER,
        )
      ) {
        return of({} as GetAccountInfoResponse);
      }
      const accountGroupId = convoDetail?.conversation.subjectParticipants.find(
        (p) => p.participantType === GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
      )?.internalParticipantId;
      if (!accountGroupId) {
        return of({} as GetAccountInfoResponse);
      }
      return this.smsService
        .getAccountInfo({
          internalId: accountGroupId,
          type: ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP,
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            console.error('error to get account info:', error.message);
            return of({} as GetAccountInfoResponse);
          }),
        );
    }),
    shareReplay(1),
  );

  draftMessages: MessageInfo | null = null;

  readonly displayBadge$ = of(!this.inboxService.isBusinessApp);
  readonly showInboxViews$ = this.inboxService.showInboxViews$;

  /**
   * Gets the active assistant for the given namespace and conversation channel.
   */
  private getActiveAssistantForChannel$(
    partnerId: string,
    accountGroupId: string,
    conversationChannel: ConversationChannel,
  ): Observable<Partial<Assistant> | null> {
    if (!this.aiAssistantService) {
      return of(null);
    }

    const namespace: Namespace = accountGroupId
      ? ({ accountGroupNamespace: { accountGroupId } } as Namespace)
      : ({ partnerNamespace: { partnerId } } as Namespace);

    if (this.isPlatformChannel(conversationChannel)) {
      return this.getPlatformConnectionAssistant$(partnerId, accountGroupId, namespace);
    }

    // Other channels: use inbox assistant lookup
    return this.getCustomerFacingAssistant$(partnerId, accountGroupId, namespace, conversationChannel);
  }

  private getPlatformConnectionAssistant$(
    partnerId: string,
    accountGroupId: string,
    namespace: Namespace,
  ): Observable<Partial<Assistant> | null> {
    return listConnections$({
      filters: { namespace },
      includeAssistantKeys: true,
    }).pipe(
      map((resp) => resp?.connections || []),
      map((connections) =>
        connections.filter(
          (conn) => conn.connectionType === ConnectionType.Platform && (conn.assistantKeys?.length ?? 0) > 0,
        ),
      ),
        switchMap((platformConnections) => {
          if (platformConnections.length === 0) {
            return of(null);
          }

          // if more than one connection, get the first one
          const connection = platformConnections[0];
          if (!connection.id || !connection.connectionType) {
            return of(null);
          }

          return (
            this.aiAssistantService
              .listAllAssistantsAssociatedToConnection(
                partnerId,
                accountGroupId,
                connection.id,
                connection.connectionType as ConnectionType,
              )
              // if more than one assistant, return the first one
              .pipe(map((assistants) => assistants[0] || null))
          );
        }),
        catchError(() => of(null)),
      );
  }

  private getCustomerFacingAssistant$(
    partnerId: string,
    accountGroupId: string,
    namespace: Namespace,
    conversationChannel: ConversationChannel,
  ): Observable<Partial<Assistant> | null> {
    const inboxConnectionTypes = [
      ConnectionType.SMS,
      ConnectionType.WhatsApp,
      ConnectionType.Facebook,
      ConnectionType.Instagram,
      ConnectionType.Email,
    ];

    // Without this the lookup returns no assistant on personal email, which hides the whole
    // conversation AI status strip (assistant name, avatar, on/off badge) on that channel. Scoped
    // to that channel because the filter below is channel-blind and takes the first match, so
    // widening it everywhere would surface the mailbox assistant on SMS and Facebook too.
    if (conversationChannel === ConversationChannel.CONVERSATION_CHANNEL_PERSONAL_EMAIL) {
      inboxConnectionTypes.push(ConnectionType.PersonalEmail);
    }

    return listConnections$({
      filters: { namespace, assistantType: AssistantType.ASSISTANT_TYPE_INBOX },
      includeAssistantKeys: true,
    }).pipe(
        map((resp) => resp?.connections || []),
        map((connections) =>
          connections.filter(
            (conn) =>
              inboxConnectionTypes.includes(conn.connectionType as ConnectionType) &&
              (conn.assistantKeys?.length ?? 0) > 0,
          ),
        ),
        switchMap((relevantConnections) => {
          if (relevantConnections.length === 0) {
            return of(null);
          }

          // if more than one connection, get the first one
          const firstConnection = relevantConnections[0];
          if (!firstConnection.id || !firstConnection.connectionType) {
            return of(null);
          }

          return this.aiAssistantService
            .listAllAssistantsAssociatedToConnection(
              partnerId,
              accountGroupId,
              firstConnection.id,
              firstConnection.connectionType as ConnectionType,
              AssistantType.ASSISTANT_TYPE_INBOX,
            )
            .pipe(
              map(
                (assistants) =>
                  assistants.find((assistant) => assistant?.type === AssistantType.ASSISTANT_TYPE_INBOX) || null,
              ),
            );
        }),
        catchError(() => of(null)),
      );
  }

  // aiResponder$ checks existence of connections associated with assistants to load assistant if applicable.
  public aiResponder$(conversationDetail: ConversationDetail) {
    return combineLatest([this.partnerId$, this.hostAppInterface.getNamespace()]).pipe(
      switchMap(([partnerId, namespace]) => {
        let accountGroupId =
          namespace.namespaceType === GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP ? namespace.id : '';

        if (namespace.namespaceType === GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_GROUP) {
          accountGroupId =
            conversationDetail?.conversation.subjectParticipants.find(
              (p) => p.participantType === GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
            )?.internalParticipantId || '';
        }
        return this.getActiveAssistantForChannel$(partnerId, accountGroupId, conversationDetail.conversation.channel);
      }),
      switchMap((activeChatAssistant: Partial<Assistant> | null) => {
        // Fetch the default assistant avatar from the default workforce if one isn't set
        if (activeChatAssistant && !activeChatAssistant.avatarUrl && this.aiAssistantService?.defaultWorkforce$) {
          return this.aiAssistantService.defaultWorkforce$.pipe(
            map((workforce) => {
              const defaultInfo = workforce?.find((d) => d.assistant.id === activeChatAssistant.id);
              if (defaultInfo?.decoration?.avatarIconUrl) {
                return { ...activeChatAssistant, avatarUrl: defaultInfo.decoration.avatarIconUrl };
              }
              return activeChatAssistant;
            }),
          );
        }
        return of(activeChatAssistant);
      }),
      map((activeChatAssistant: Partial<Assistant> | null): AiResponder => {
        const aiConfig = conversationDetail.conversation.aiConfiguration;
        // Read the mode, not the legacy bool: the backend writes ai_response_enabled = (mode ==
        // AutoSend), so AutoDraft would otherwise look disabled to the AI status strip.
        const aiMode = resolveCurrentAiMode(
          toAiResponseMode((aiConfig as { aiResponseMode?: unknown } | undefined)?.aiResponseMode),
          aiConfig?.aiResponseEnabled ?? false,
        );
        return {
          isEnabled: !!activeChatAssistant,
          assistant: activeChatAssistant,
          isConversationEnabled: aiMode !== AIResponseMode.AI_RESPONSE_MODE_OFF,
          aiMode,
        };
      }),
    );
  }

  readonly aiResponderForConversation$ = this.currentConversationDetail$.pipe(
    distinctUntilChanged(
      (a, b) =>
        a?.conversation.conversationId === b?.conversation.conversationId &&
        a?.conversation.aiConfiguration?.aiResponseEnabled === b?.conversation.aiConfiguration?.aiResponseEnabled,
    ),
    switchMap((conversationDetail) => {
      if (!conversationDetail) return of(null);

      if (this.isPlatformChannel(conversationDetail.conversation.channel) && this.inboxService.isPartnerCenter) {
        const isPartnerToPartner = conversationDetail.conversation.subjectParticipants.every(
          (participant) => participant.participantType === GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
        );

        if (isPartnerToPartner) {
          return this.aiResponder$(conversationDetail);
        }
        return of(null);
      }

      // if is not a conversation with a customer, return false
      const customer = conversationDetail.conversation.subjectParticipants.some(
        (participant) => participant.participantType === GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_CUSTOMER,
      );

      if (!customer) return of(null);

      return this.aiResponder$(conversationDetail);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private isPlatformChannel(channel: ConversationChannel): boolean {
    return channel === ConversationChannel.CONVERSATION_CHANNEL_INTERNAL;
  }

  constructor(
    private readonly conversationApiService: ConversationApiService,
    private readonly snackbarService: SnackbarService,
    private readonly analyticsService: ProductAnalyticsService,
    @Inject(USER_ID_TOKEN) readonly userId$: Observable<string>,
    @Inject(ACCOUNT_GROUP_ID_TOKEN) private readonly accountGroupId$: Observable<string>,
    @Inject(PARTNER_ID_TOKEN) private readonly partnerId$: Observable<string>,
    @Inject(CONVERSATION_CONVERSATION_CHANNELS_ENABLED_TOKEN)
    private readonly conversationChannelsEnabled$: Observable<ConversationChannel[]>,
    @Inject(MARKET_ID_TOKEN) readonly marketId$: Observable<string>,
    private readonly participantService: ParticipantService,
    private readonly inboxService: InboxService,
    private readonly translateService: TranslateService,
    private readonly firestoreService: FirestoreService,
    @Inject(CONVERSATION_CHANNEL_SERVICE_TOKEN)
    private readonly conversationChannelServices: Record<ConversationChannel, ConversationChannelService | undefined>,
    private readonly smsService: SmsService,
    @Inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN) private readonly hostAppInterface: HostAppInterface,
    @Inject(CONVERSATION_COUNTRY_TOKEN) readonly country$: Observable<string>,
    @Optional() private readonly aiAssistantService: AiAssistantService,
    private readonly conversationStatelessService: ConversationStatelessService,
    @Inject(CONVERSATION_EMAIL_ENABLED_TOKEN) private readonly emailEnabled$: Observable<boolean>,
    private readonly paginatedMessagesService: PaginatedConversationMessagesService,
  ) {
    this.conversationChannelsEnabled$.pipe(takeUntilDestroyed()).subscribe((conversationChannelsEnabled) => {
      if (!conversationChannelsEnabled || conversationChannelsEnabled.length === 0) {
        console.warn(
          'You need to set up the conversationChannelsEnabled in the ConversationConfig. To fix see documentation in the README of @galaxy/conversation/core',
        );
      }

      if (conversationChannelsEnabled && conversationChannelsEnabled.length > 0) {
        this.setConversationChannel(conversationChannelsEnabled[0]);
      }
    });

    this.paginationSync$.pipe(takeUntilDestroyed()).subscribe();

    this.messages$.pipe(takeUntilDestroyed()).subscribe((messages) => {
      const multipleParticipantsHaveMessages = (messages || []).some(
        (message) => !!message.sender && message.sender.participantId !== messages[0].sender?.participantId,
      );
      if (multipleParticipantsHaveMessages) {
        this.analyticsService.trackEvent('inbox', 'biDirectionConversation', 'view');
      }
    });

    combineLatest([this.messages$, this.currentFirestoreConversationId$])
      .pipe(
        takeUntilDestroyed(),
        filter(([messages, conversationId]) => !!conversationId && messages?.length > 0),
        distinctUntilChanged(
          ([prevMessages, prevId], [currMessages, currId]) =>
            prevId === currId && prevMessages[0]?.id === currMessages[0]?.id,
        ),
      )
      .subscribe(([_, conversationId]) => {
        this.setConversationLastSeen(
          fromFirestoreId(conversationId),
          SetLastSeenRequestStatus.SET_LAST_SEEN_REQUEST_STATUS_READ,
        );
      });
  }

  private setConversationChannel(conversationChannel: ConversationChannel): void {
    this.currentConversationChannel = conversationChannel;
  }

  getConversationChannel(): ConversationChannel {
    return this.currentConversationChannel;
  }

  async sendAndStageMessage(
    conversationId: string,
    type: MessageType,
    body: string,
    channel: ConversationChannel,
    originLocation: PlatformLocation,
    media: (MediaInterface & { transcodingJobId?: string })[] = [],
    recipient?: Participant,
    sender?: Participant,
    metadata?: {
      identifier: MetadataIdentifier;
      data: {
        [key: string]: string;
      };
    }[],
    skipMessageDelivery?: boolean,
    aiMode?: AIMode,
  ): Promise<SendMessageResponse> {
    if (!sender) {
      sender = await firstValueFrom(this.participantService.currentParticipant$);
    }

    try {
      const result: SendMessageResponse = await this.conversationStatelessService.sendMessage(
        conversationId,
        type,
        body,
        channel,
        originLocation,
        media,
        sender,
        recipient,
        metadata,
        skipMessageDelivery,
        aiMode,
      );
      if (!result) {
        return result;
      }

      const now = new Date();
      const isVideoSend = media.some((m) => m.transcodingJobId);
      const newMessage: ConversationMessage = {
        id: result.messageId.replace('MESSAGE-', ''),
        messageId: result.messageId,
        conversationId: conversationId,
        sender,
        type: type,
        channel,
        body,
        media: media as Media[],
        created: now,
        // Video enters PRESEND — sendStatus with no status field satisfies
        // isPreSendProcessing(), showing the processing bubble immediately.
        sendStatus: isVideoSend
          ? { created: now }
          : { created: now, status: MessageStatus.MESSAGE_STATUS_SENDING },
      };
      this.addUnsavedMessage(toFirestoreId(conversationId), newMessage);
      return result;
    } catch (httpErrorResponse) {
      console.error('ConversationService sendSMS error', httpErrorResponse);
      throw httpErrorResponse;
    }
  }

  /**
   * set current firestore conversation ID
   * @param {string} conversationId - An conversation ID
   */
  setCurrentFirestoreConversationId(conversationId: string): void {
    this.currentFirestoreConversationId$$.next(conversationId ?? '');
  }

  /**
   * get the preview content of the conversation with message sender name
   * @param {ConversationDetail} conversationDetail - conversationDetail
   * @return {string} - the preview content with message sender name
   */
  getPreviewContent(conversationDetail: ConversationDetail): Observable<string> {
    return this.userId$.pipe(
      map((userId) => {
        const previewContent = this.getPreviewContentText(conversationDetail);
        const latestMessageParticipant = conversationDetail?.participants?.find(
          (participant) => participant?.participantId === conversationDetail.message?.participantId,
        );
        if (latestMessageParticipant?.isParticipantInternalInfoDeleted) {
          return `${this.translateService.instant('INBOX.PREVIEW_PANE.DELETED_USER')}: ${previewContent}`;
        }
        const senderLabel = INVALID_CONTACT_NAMES.includes(latestMessageParticipant?.name)
          ? latestMessageParticipant?.phoneNumber
          : latestMessageParticipant?.name;
        const otherUserPreviewContent = !senderLabel
          ? `${previewContent}`
          : `${senderLabel}: ${previewContent}`;
        return latestMessageParticipant?.internalParticipantId === userId
          ? `${this.translateService.instant('INBOX.PREVIEW_PANE.CURRENT_USER')}: ${previewContent}`
          : otherUserPreviewContent;
      }),
    );
  }

  /**
   * get the preview text of the conversation according to the message body text and media info
   * @param {ConversationDetail} conversationDetail - conversationDetail
   * @return {string} - the preview text without message sender name
   */
  getPreviewContentText(conversationDetail: ConversationDetail): string {
    if (conversationDetail?.message) {
      if (conversationDetail?.message?.body) {
        return new DOMParser()
          .parseFromString(conversationDetail.message.body, 'text/html')
          .body.textContent?.trim() ?? '';
      }

      // If message only has media without text
      if (conversationDetail?.message?.media?.length > 0) {
        return this.translateService.instant('INBOX.PREVIEW_PANE.SHARED_A_FILE');
      }
    }

    if (conversationDetail?.event) {
      const label = '[' + this.translateService.instant(conversationDetail.event.labelKey) + ']';
      if (conversationDetail.event.type === EventType.EVENT_TYPE_PHONE_CALL) {
        return label + ' ' + conversationDetail.event.message;
      }
      return label;
    }

    return '';
  }

  /**
   * get a message from the Conversation µs
   * @param {string} messageId - the message ID
   * @return {Observable<Message>}
   */
  getMessage(messageId: string): Observable<Message> {
    if (!messageId || messageId === '') {
      console.error('the messageId is not valid');
      return of({} as Message);
    }

    const req = {
      messageId,
    } as GetMessageRequest;

    return this.conversationApiService.getMessage(req).pipe(
      catchError((err) => {
        console.error('getMessage error:', err?.message);
        return of({} as Message);
      }),
    );
  }

  /**
   * set conversation lastSeenTime by Participant
   * @param {string} conversationId - the conversation ID
   * @param {SetLastSeenRequestStatus} status - the timestamp
   */
  async setConversationLastSeen(conversationId: string, status: SetLastSeenRequestStatus): Promise<void> {
    if (conversationId) {
      this.updateLocalReadOverride(
        conversationId,
        status === SetLastSeenRequestStatus.SET_LAST_SEEN_REQUEST_STATUS_READ,
      );
    }

    const participant = await firstValueFrom(this.currentParticipant$);
    await firstValueFrom(
      setLastSeen$({
        conversationId,
        participant: participant as unknown as ProtoParticipant,
        status,
      }),
    );
  }

  async setConversationOpenStatus(conversationId: string, isOpen: boolean): Promise<Conversation> {
    // Optimistically reflect the new state so the conversation leaves the
    // mismatched view immediately, ahead of the eventually-consistent index.
    this.updateLocalOpenStatusOverride(conversationId, isOpen);
    try {
      const resp = await firstValueFrom(
        this.conversationApiService.updateConversation({
          conversation: {
            conversationId: conversationId,
            isOpen: isOpen,
          },
          fieldMask: {
            paths: ['is_open'],
          },
        }),
      );

      this.snackbarService.openSuccessSnack(
        isOpen ? 'INBOX.INFO.CONVERSATION_STATUS_OPENED' : 'INBOX.INFO.CONVERSATION_STATUS_CLOSED',
      );

      return resp.conversation;
    } catch (err) {
      this.clearOpenStatusOverride(conversationId);
      throw err;
    }
  }

  async setConversationAIResponderStatus(conversationId: string, status: boolean): Promise<Conversation> {
    const resp = await firstValueFrom(
      this.conversationApiService.updateConversation({
        conversation: {
          conversationId: conversationId,
          aiConfiguration: {
            aiResponseEnabled: status,
          },
        },
        fieldMask: {
          paths: ['ai_response_enabled'],
        },
      }),
    );

    this.snackbarService.openSuccessSnack(
      status ? 'INBOX.INFO.AI_RESPONSES_ENABLED' : 'INBOX.INFO.AI_RESPONSES_DISABLED',
    );

    return resp.conversation;
  }

  /**
   * Sets the conversation's AI response mode (Off / AutoSend / AutoDraft) — the tri-state successor
   * to setConversationAIResponderStatus. Uses the generated updateConversation$ because the
   * @vendasta/conversation class SDK's AIConfiguration does not yet expose aiResponseMode.
   */
  async setConversationAIResponseMode(conversationId: string, mode: AIResponseMode): Promise<void> {
    await firstValueFrom(
      updateConversation$({
        conversation: {
          conversationId,
          aiConfiguration: { aiResponseMode: mode },
        },
        // Backend permits the 'ai_configuration' mask (not 'ai_response_mode'); its handler maps
        // aiResponseMode → AIMode and rejects AutoSend on personal email.
        fieldMask: { paths: ['ai_configuration'] },
      }),
    );
    this.snackbarService.openSuccessSnack('INBOX.INFO.AI_MODE_UPDATED');
  }

  /** Assigns a conversation to one or more users. Pass `[]` to clear. */
  assignConversation$(conversationId: string, assigneeUserIds: string[]): Observable<void> {
    if (!conversationId) return throwError(() => new Error('conversationId is required'));
    return assignConversation$({ conversationId, assigneeUserIds }).pipe(
      map(() => void 0),
    );
  }

  async wakeUpAIResponder(conversationId: string): Promise<void> {
    await firstValueFrom(
      cancelAIResponder$({
        conversationId: conversationId,
      }),
    );
  }

  conversationUnseen(conversation: Conversation): Observable<boolean> {
    return combineLatest([this.currentParticipant$, this.localReadOverrides$$]).pipe(
      map(([participant, readOverrides]) => {
        const id = conversation?.conversationId;
        const unseenByRpc = conversationUnseen(conversation, participant?.participantId);
        if (readOverrides.has(id)) {
          return { unseen: false, pruneId: unseenByRpc ? null : id };
        }
        return { unseen: unseenByRpc, pruneId: null };
      }),
      tap(({ pruneId }) => {
        if (pruneId) {
          this.updateLocalReadOverride(pruneId, false);
        }
      }),
      map(({ unseen }) => unseen),
    );
  }

  private updateLocalReadOverride(
    conversationId: string,
    isRead: boolean,
  ): void {
    const s = new Set(this.localReadOverrides$$.value);
    if (isRead) {
      s.add(conversationId);
    } else {
      s.delete(conversationId);
    }
    this.localReadOverrides$$.next(s);
  }

  private updateLocalOpenStatusOverride(conversationId: string, isOpen: boolean): void {
    const m = new Map(this.localOpenStatusOverrides$$.value);
    m.set(conversationId, isOpen);
    this.localOpenStatusOverrides$$.next(m);
  }

  clearOpenStatusOverride(conversationId: string): void {
    if (this.localOpenStatusOverrides$$.value.has(conversationId)) {
      const m = new Map(this.localOpenStatusOverrides$$.value);
      m.delete(conversationId);
      this.localOpenStatusOverrides$$.next(m);
    }
  }

  isUserImpersonated$ = inject(CONVERSATION_CONFIG_TOKEN, { optional: true })?.isImpersonating$ || of(false);

  private readonly personalEmailContentService = inject(PersonalEmailContentService);

  getConversationEvents(docId: string): Observable<Event[]> {
    const conversationId = fromFirestoreId(docId);
    return this.firestoreService.events$(docId).pipe(
      switchMap((events: FirestoreEvent[]) => {
        const eventIds: string[] = events.map((m) => m.eventId).filter((id): id is string => !!id);
        return getMultiEvents$({ eventIds, conversationId, excludeInternalEvents: true }).pipe(
          map((res) => (res.events ?? []).map((event) => this.toLegacyEvent(event))),
          catchError((error) => {
            console.error('error to call getMultiEvents', error);
            return of(new Array<Event>());
          }),
        );
      }),
    );
  }

  private toLegacyEvent(event: ProtoEvent): Event {
    return {
      ...event,
      channel: ConversationChannelValue[event.channel] ?? ConversationChannelValue.CONVERSATION_CHANNEL_UNDEFINED,
      type: EventTypeValue[event.type] ?? EventTypeValue.EVENT_TYPE_UNDEFINED,
    } as unknown as Event;
  }

  getUnreadConversationsCount(viewId: string): Observable<number> {
    return this.currentParticipant$.pipe(
      switchMap((participant) =>
        this.countUnreadConversations(
          this.firestoreService.getUnreadConversationsByView(participant?.participantId, viewId),
        ),
      ),
    );
  }

  countUnreadConversations(conversationsCollection: Query<FirestoreConversation>): Observable<number> {
    return collectionData(conversationsCollection, { idField: 'id' }).pipe(
      map((cs) => cs.map((c) => c.conversationId).filter((id): id is string => !!id)),
      switchMap((ids) => of(ids.length)),
    );
  }

  removeLastConversationId(): void {
    localStorage.removeItem(LOCAL_STORAGE_ID);
    this.currentFirestoreConversationId$$.next('');
  }

  conversationSelected(selected: boolean): void {
    this.conversationSelected$$.next(selected);
  }

  updateButtonClickState(messageId: string, buttonId: string, clickedAt: Date): void {
    this.buttonClickStates.update((currentStates) => ({
      ...currentStates,
      [messageId]: {
        ...currentStates[messageId],
        [buttonId]: clickedAt,
      },
    }));
  }

  private readonly paginationSync$ = this.currentFirestoreConversationId$.pipe(
    tap((docId) => {
      this.paginatedMessagesService.setConversationId(docId ? fromFirestoreId(docId) : '');
    }),
  );

  private readonly messagesScrollable$: Observable<{
    messages: ConversationMessage[];
    events: Event[];
    loading: boolean;
  }> = this.currentFirestoreConversationId$.pipe(
    observeOn(asyncScheduler),
    switchMap((docId) => {
      if (!docId) {
        return of({ messages: [], events: [], loading: false });
      }
      const conversationId = fromFirestoreId(docId);
      const transformed$ = combineLatest([
        this.currentConversationDetail$,
        this.paginatedMessagesService.messages$,
        this.paginatedMessagesService.loadingFirstPage$,
      ]).pipe(
        switchMap(([conversationDetail, messages, loading]) => {
          if (conversationDetail?.conversation?.conversationId !== conversationId) {
            return EMPTY;
          }
          const conversationMessages: ConversationMessage[] = (messages || [])
            .filter((m): m is Message => !!m && !!m.messageId)
            .map((m) =>
              convertMessageIntoConversationMessage(
                m,
                conversationDetail.participants.find((p) => p.participantId === m.participantId),
              ),
            )
            .filter((m): m is ConversationMessage =>
              Boolean(m && (m.sender || m.type === MessageType.MESSAGE_TYPE_SYSTEM)),
            );
          return this.hostAppInterface.getNamespace().pipe(
            first(),
            switchMap((namespace) =>
              this.personalEmailContentService.enrichMessages(conversationMessages, namespace.id),
            ),
            catchError(() => of(conversationMessages)),
            map((enriched) => ({ messages: enriched, loading })),
          );
        }),
      );

      const merged$ = combineLatest([transformed$, this.unsavedMessages$$]).pipe(
        map(([{ messages, loading }, unsavedMessages]) => {
          const unsavedMessagesForDocId = unsavedMessages[docId] || [];
          const filterSavedMessages = unsavedMessagesForDocId.filter(
            (msg) => !messages.find((m) => m.messageId === msg.messageId),
          );
          return { messages: [...filterSavedMessages, ...messages], loading };
        }),
      );

      return combineLatest([merged$, this.getConversationEvents(docId)]).pipe(
        map(([{ messages, loading }, events]) => ({ messages, events, loading })),
        startWith({ messages: [] as ConversationMessage[], events: [] as Event[], loading: true }),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  // Original messages observable (kept for backward compatibility) will refactor it later
  public readonly messages$ = this.messagesScrollable$.pipe(map((res) => res.messages));

  private readonly baseMessagesSignal = toSignal(this.messages$, { initialValue: [] as ConversationMessage[] });

  public readonly messagesSignal = computed(() => {
    const baseMessages = this.baseMessagesSignal();
    const buttonClickStates = this.buttonClickStates();

    return this.applyButtonClickStates(baseMessages, buttonClickStates);
  });

  /**
   * Applies button click states to a list of messages
   * @param messages - The base messages to apply button states to
   * @param buttonClickStates - The button click state mapping
   * @returns Messages with updated button click states
   */
  private applyButtonClickStates(
    messages: ConversationMessage[],
    buttonClickStates: { [messageId: string]: { [buttonId: string]: Date } },
  ): ConversationMessage[] {
    return messages.map((message) => {
      if (!message.UIComponents || !message.messageId) return message;

      const messageClickStates = buttonClickStates[message.messageId];
      if (!messageClickStates) return message;

      const updatedUIComponents = message.UIComponents.map((component) => {
        if (!component.button?.id) return component;

        const clickedAt = messageClickStates[component.button.id];
        if (clickedAt) {
          return {
            ...component,
            button: {
              ...component.button,
              clickedAt,
            },
          };
        }
        return component;
      });

      return {
        ...message,
        UIComponents: updatedUIComponents,
      };
    });
  }
  public readonly events$ = this.messagesScrollable$.pipe(map((res) => res.events));
  public readonly loadingMessages$ = this.messagesScrollable$.pipe(map((res) => res.loading));

  public readonly paginatedHasMore$ = this.paginatedMessagesService.hasMore$;
  public readonly paginatedLoadingOlder$ = this.paginatedMessagesService.loadingOlder$;

  loadOlderMessages(): void {
    this.paginatedMessagesService.loadOlder();
  }

  private readonly refetchAvailableChannels$ = this.messages$.pipe(
    distinctUntilChanged((prev, curr) => prev[0]?.id === curr[0]?.id),
    filter((messages) => messages.length > 0),
    map((messages) => messages[0]),
    filter(
      (message) =>
        (message.channel === ConversationChannel.CONVERSATION_CHANNEL_WHATSAPP ||
          message.channel === ConversationChannel.CONVERSATION_CHANNEL_FACEBOOK ||
          message.channel === ConversationChannel.CONVERSATION_CHANNEL_INSTAGRAM) &&
        message.sender?.participantType === pType.PARTICIPANT_TYPE_CUSTOMER,
    ),
    map(() => null),
    startWith(null),
  );

  private readonly availableChannelsForConversation$: Observable<GetAvailableChannelsForConversationResponse | null> =
    combineLatest([this.currentFirestoreConversationId$, this.refetchAvailableChannels$]).pipe(
      filter(([conversationID]) => !!conversationID),
      switchMap(([conversationID]) => {
        return this.conversationApiService
          .getAvailableChannelsForConversation({
            conversationId: fromFirestoreId(conversationID),
          })
          .pipe(catchError(() => of(null)));
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      distinctUntilChanged(),
    );

  private readonly filteredChannels$: Observable<ConversationChannel[]> = this.availableChannelsForConversation$.pipe(
    switchMap((availableChannelsForConversation) => {
      if (!availableChannelsForConversation) {
        return of(
          new GetAvailableChannelsForConversationResponse({
            channels: new Array<ConversationChannel>(),
            preferredChannel: ConversationChannel.CONVERSATION_CHANNEL_UNDEFINED,
          }),
        );
      }
      return of(availableChannelsForConversation);
    }),
    map((res) => res?.channels ?? new Array<ConversationChannel>()),
    switchMap((channels) =>
      this.emailEnabled$.pipe(
        map((emailEnabled) => {
          if (this.inboxService.isPartnerCenter || !emailEnabled) {
            return channels.filter((channel) => channel !== ConversationChannel.CONVERSATION_CHANNEL_EMAIL);
          }
          return channels;
        }),
      ),
    ),
    switchMap((channels) => {
      if (this.hostAppInterface.getAppOptions().is_multilocation) {
        return this.currentConvoSMSNumber$.pipe(
          map((convoSMSNumber) => {
            if (convoSMSNumber) {
              return channels;
            } else {
              return channels.filter((channel) => channel !== ConversationChannel.CONVERSATION_CHANNEL_SMS);
            }
          }),
        );
      }

      // TODO: MEGA-791 - Remove this once SMS registration state check is implemented on backend
      return this.inboxService.isSMSChannelAvailableForOrg$.pipe(
        map((isSMSChannelAvailable) => {
          return isSMSChannelAvailable
            ? channels
            : channels.filter((channel) => channel !== ConversationChannel.CONVERSATION_CHANNEL_SMS);
        }),
      );
    }),
    catchError(() => of(new Array<ConversationChannel>())),
    distinctUntilChanged((prev, curr) => {
      if (prev.length === 0 && curr.length === 0) {
        return true;
      }
      return curr === prev;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  //TODO: MEGA-791 Remove the filteredChannels dependency once we migrate the access control logic to the backend.
  //The entire channel availability will be in the backend.
  readonly availableChannels$: Observable<ConversationAvailableChannels> = combineLatest([
    this.availableChannelsForConversation$,
    this.filteredChannels$,
  ]).pipe(
    map(([availableChannelsForConversation, filteredAvailableChannels]): ConversationAvailableChannels => {
      if (
        !availableChannelsForConversation ||
        availableChannelsForConversation?.preferredChannel === ConversationChannel.CONVERSATION_CHANNEL_UNDEFINED
      ) {
        return {
          preferredChannel: ConversationChannel.CONVERSATION_CHANNEL_UNDEFINED,
          channelAvailabilities: availableChannelsForConversation?.channelAvailabilities ?? [],
          availableChannels: filteredAvailableChannels,
        };
      }
      if (filteredAvailableChannels.includes(availableChannelsForConversation?.preferredChannel)) {
        return {
          preferredChannel: availableChannelsForConversation.preferredChannel,
          channelAvailabilities: availableChannelsForConversation?.channelAvailabilities ?? [],
          availableChannels: filteredAvailableChannels,
        };
      }
      return {
        preferredChannel: filteredAvailableChannels[0],
        channelAvailabilities: availableChannelsForConversation?.channelAvailabilities ?? [],
        availableChannels: filteredAvailableChannels,
      };
    }),
    distinctUntilChanged(),
  );

  displayAlertIfExists(
    conversationDetail: ConversationDetail | null,
    conversationChannel: ConversationChannel,
  ): Observable<AlertOptions | null> {
    const service = this.conversationChannelServices[conversationChannel];
    if (!service) return of(null);
    if (!conversationDetail) return of(null);

    return combineLatest([this.partnerId$, this.accountGroupId$, this.availableChannels$]).pipe(
      switchMap(([partnerId, accountGroupId, channels]) => {
        const isThread = !!getMainConversationId(conversationDetail);
        if (channels.availableChannels?.length > 0 && !isThread) return of(null);
        return service.getChatComposerAlert(
          partnerId,
          accountGroupId,
          conversationDetail ?? undefined,
          channels.availableChannels as ConversationChannel[],
          channels.channelAvailabilities,
        );
      }),
    );
  }

  private readonly unsavedMessages$$ = new BehaviorSubject<{ [docId: string]: ConversationMessage[] }>({});
  private readonly buttonClickStates = signal<{ [messageId: string]: { [buttonId: string]: Date } }>({});

  private addUnsavedMessage(docId: string, message: ConversationMessage) {
    const unsavedMessages = this.unsavedMessages$$.getValue();
    const unsavedMessagesForDocId = unsavedMessages[docId] || [];
    this.unsavedMessages$$.next({
      ...unsavedMessages,
      [docId]: [message, ...unsavedMessagesForDocId],
    });
  }

  // TODO(TIF-21 Phase 1): Remove mockVideo query param and mock paths
  addMockUnsavedMessage(message: ConversationMessage) {
    const docId = this.currentFirestoreConversationId$$.getValue();
    this.addUnsavedMessage(toFirestoreId(docId), message);
  }

  // TODO(TIF-21 Phase 1): Remove mockVideo query param and mock paths
  removeMockUnsavedMessage(messageId: string) {
    const docId = toFirestoreId(this.currentFirestoreConversationId$$.getValue());
    const unsavedMessages = this.unsavedMessages$$.getValue();
    const filtered = (unsavedMessages[docId] || []).filter((m) => m.messageId !== messageId);
    this.unsavedMessages$$.next({
      ...unsavedMessages,
      [docId]: filtered,
    });
  }

  // TODO(TIF-21 Phase 1): Remove mockVideo query param and mock paths
  updateMockUnsavedMessage(messageId: string, updates: Partial<ConversationMessage>) {
    const docId = toFirestoreId(this.currentFirestoreConversationId$$.getValue());
    const unsavedMessages = this.unsavedMessages$$.getValue();
    const messages = (unsavedMessages[docId] || []).map((m) =>
      m.messageId === messageId ? { ...m, ...updates } : m,
    );
    this.unsavedMessages$$.next({
      ...unsavedMessages,
      [docId]: messages,
    });
  }

  /**
   * evaluate the helpfulness of a response
   * @param {string} messageId - the id of the message response to evaluate
   * @param {string} eventId - the id of the event response to evaluate
   * @param {string} conversationId - the id of the conversation
   * @param {string} comment - a comment about the helpfulness of the response
   * @param {EvaluationSentiment} sentiment - the sentiment of the evaluation
   */

  // for event evaluation
  async evaluateResponse(
    comment: string,
    sentiment: EvaluationSentiment,
    messageId: string,
    eventId: string,
    conversationId: string,
  ): Promise<void>;

  // for message evaluation
  async evaluateResponse(comment: string, sentiment: EvaluationSentiment, messageId: string): Promise<void>;

  async evaluateResponse(
    comment: string,
    sentiment: EvaluationSentiment,
    messageId?: string,
    eventId?: string,
    conversationId?: string,
  ): Promise<void> {
    let req: EvaluateResponseRequestInterface;
    if (messageId) {
      req = {
        evaluatedMessageId: messageId,
        comment: comment,
        sentiment: sentiment,
      };
    } else {
      req = {
        evaluatedEventId: eventId,
        conversationId: conversationId,
        comment: comment,
        sentiment: sentiment,
      };
    }
    await firstValueFrom(this.conversationApiService.evaluateResponse(req));
  }
}
